/**
 * StageSelect — a tester's stage picker: a "TESTER: STAGE SELECT" link that
 * opens a panel with < STAGE N > and PLAY. Shown for comp accounts (the
 * server lets them start a game on any stage) and in free-play builds.
 * ← → change the stage by 1, ↑ ↓ by 10, ENTER plays, ESC closes.
 */

import { FONT_SIZE, fontStyle } from "../FontScale";
import { fitText, stack } from "./layout";
import { FINAL_STAGE } from "../../shared/stage/arcade";

const DEPTH = 900;
const clampStage = (n) => Math.min(FINAL_STAGE, Math.max(1, n));

/**
 * @param {Phaser.Scene} scene
 * @param {{ x: number, y: number, onPick: (stage: number) => void }} p - link position (bottom-left origin)
 */
export function addStageSelectLink(scene, { x, y, onPick }) {
  const link = scene.add
    .text(x, y, "TESTER: STAGE SELECT", fontStyle(FONT_SIZE.CAPTION, "#39FF14"))
    .setOrigin(0, 1)
    .setDepth(DEPTH)
    .setInteractive({ useHandCursor: true });
  link.on("pointerdown", () => openPanel(scene, onPick));
  return link;
}

function openPanel(scene, onPick) {
  if (scene._stageSelectOpen) return;
  scene._stageSelectOpen = true;
  const W = scene.scale.width;
  const H = scene.scale.height;
  const CX = W / 2;
  const parts = [];
  const add = (content, size, color) => {
    const t = fitText(scene.add.text(CX, 0, content, fontStyle(size, color)).setOrigin(0.5, 0).setDepth(DEPTH + 2), W - 40);
    parts.push(t);
    return t;
  };
  let stage = 1;

  const shade = scene.add.rectangle(0, 0, W, H, 0x000000, 0.7).setOrigin(0).setDepth(DEPTH).setInteractive();
  parts.push(shade);
  const title = add("STAGE SELECT", FONT_SIZE.HEADING, "#39FF14");
  const picker = add("", FONT_SIZE.TITLE, "#FFD84A");
  const hint = add("← → 1   ↑ ↓ 10   (TESTER GAMES DON'T COUNT)", FONT_SIZE.CAPTION, "#AAAAAA");
  const play = add("PLAY", FONT_SIZE.HEADING, "#7BC74A").setInteractive({ useHandCursor: true });
  const close = add("CLOSE", FONT_SIZE.BODY, "#AAAAAA").setInteractive({ useHandCursor: true });
  const minus = scene.add.text(0, 0, "<", fontStyle(FONT_SIZE.TITLE, "#00FFFF")).setOrigin(0.5, 0).setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
  const plus = scene.add.text(0, 0, ">", fontStyle(FONT_SIZE.TITLE, "#00FFFF")).setOrigin(0.5, 0).setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
  parts.push(minus, plus);

  const top = H * 0.3;
  const bottom = stack([title, picker, hint, play, close], top + 16, 12);
  const panelW = Math.min(420, W - 24);
  const panel = scene.add.graphics().setDepth(DEPTH + 1);
  panel.fillStyle(0x000000, 0.95);
  panel.fillRoundedRect(CX - panelW / 2, top, panelW, bottom - top + 16, 10);
  panel.lineStyle(2, 0x39ff14, 0.8);
  panel.strokeRoundedRect(CX - panelW / 2, top, panelW, bottom - top + 16, 10);
  parts.push(panel);
  minus.setPosition(CX - panelW / 2 + 36, picker.y);
  plus.setPosition(CX + panelW / 2 - 36, picker.y);

  const show = () => picker.setText(`STAGE ${stage}`);
  const step = (d) => {
    stage = clampStage(stage + d);
    show();
  };
  // Held keys auto-repeat: one step per press
  const once = (fn) => (e) => {
    if (!e?.repeat) fn();
  };
  const keys = {
    "keydown-LEFT": once(() => step(-1)),
    "keydown-RIGHT": once(() => step(1)),
    "keydown-DOWN": once(() => step(-10)),
    "keydown-UP": once(() => step(10)),
    "keydown-ENTER": () => done(() => onPick(stage)),
    "keydown-ESC": () => done(() => {}),
  };
  const done = (next) => {
    Object.entries(keys).forEach(([evt, fn]) => scene.input.keyboard.off(evt, fn));
    parts.forEach((p) => p.destroy());
    scene._stageSelectOpen = false;
    next();
  };
  Object.entries(keys).forEach(([evt, fn]) => scene.input.keyboard.on(evt, fn));
  minus.on("pointerdown", () => step(-1));
  plus.on("pointerdown", () => step(1));
  play.on("pointerdown", keys["keydown-ENTER"]);
  close.on("pointerdown", keys["keydown-ESC"]);
  show();
}
